import { Badge, Box, Flex, Text, Icon, Image, useDisclosure, Button } from '@chakra-ui/react';
import { CalendarIcon, ClockIcon, MapPin, ExternalLink, Building2Icon, Share2 } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import parse from 'html-react-parser';
import { ReactComponent as PlaceholderTeamLogo } from 'assets/placeholder.svg';
import { TeamInfo } from 'api/responseTypes';
import { LocationMapModal } from 'components/LocationMapModal/LocationMapModal';
import { useEventTracking } from 'hooks/useEventTracking';
import { shareEvent } from 'utils/shareEvent';

type EventHeaderMatchProps = {
  variant: 'match';
  date?: string | null;
  time?: string;
  stadium?: string | null;
  teamA: TeamInfo;
  teamB: TeamInfo;
  city?: string;
  arDescription?: string | null;
  locationHtml?: string | null;
  eventKey: string;
  eventName: string;
  isFreeEvent?: boolean;
  hasTimeslots?: boolean;
};

type EventHeaderConcertProps = {
  variant: 'concert';
  title: string;
  subtitle?: string;
  imageLink: string;
  date?: string | null;
  time?: string;
  stadium?: string | null;
  city?: string;
  arDescription?: string | null;
  locationHtml?: string | null;
  eventKey: string;
  eventName: string;
  isFreeEvent?: boolean;
  hasTimeslots?: boolean;
};

type EventHeaderInternationalProps = {
  variant: 'international';
  title: string;
  subtitle?: string;
  imageLink: string;
  date?: string | null;
  time?: string;
  stadium?: string | null;
  city?: string;
  arDescription?: string | null;
  locationHtml?: string | null;
  eventKey: string;
  eventName: string;
  isFreeEvent?: boolean;
  hasTimeslots?: boolean;
};

type EventHeaderProps =
  | EventHeaderMatchProps
  | EventHeaderConcertProps
  | EventHeaderInternationalProps;

export const EventHeader = (props: EventHeaderProps) => {
  const isMatch = props.variant === 'match';
  const isConcert = props.variant === 'concert';
  const isInternational = props.variant === 'international';
  // Concert + international share the same image/title/details layout.
  const isConcertLike = isConcert || isInternational;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { eventTracking } = useEventTracking();

  const locationName = props.stadium as string;

  // International shows its city via the Building2 row only (no venue map row).
  const hasLocation = isMatch ? props.stadium : isInternational ? false : props.city;
  const hasLocationMap = Boolean(props.locationHtml);

  const handleLocationClick = () => {
    if (hasLocationMap) {
      onOpen();
    }
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent) => {
    if (hasLocationMap && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onOpen();
    }
  };

  const handleShareClick = async () => {
    if (isInternational) {
      eventTracking.internationalEvent.shared({ eventName: props.eventName });
    } else {
      eventTracking.share.buttonClicked({
        typeOfEvent: isMatch ? 'match' : 'concert',
        eventName: props.eventName,
      });
    }
    try {
      await shareEvent(
        props.eventName,
        props.eventKey,
        isInternational ? 'international' : isConcert,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '';

      if (errorMessage.includes('AbortError') || errorMessage.includes('cancel')) {
        return;
      }
    }
  };

  const handleShareKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleShareClick();
    }
  };

  return (
    <Box w='100%' px={{ base: '4', md: '6' }} py={{ base: '4', md: '6' }}>
      <Box
        display='flex'
        flexDirection={{ base: 'column', md: 'row' }}
        overflow='hidden'
        maxW='1440px'
        mx='auto'
        borderWidth='1px'
        borderRadius={{ base: 'lg', md: 'xl' }}
        boxShadow={{ base: 'sm', md: 'lg' }}
        bg='white'
        w='100%'
        transition='all 0.3s ease'
        _hover={{
          boxShadow: { base: 'md', md: 'xl' },
        }}
      >
        {/* Visual Section - Teams or Concert Image */}
        <Box
          w={{ base: '100%', md: isMatch ? 'auto' : '400px', lg: isMatch ? 'auto' : '450px' }}
          minW={{ base: '100%', md: isMatch ? 'auto' : '300px' }}
          flexShrink={0}
          p={{ base: '4', md: isMatch ? '4' : '3' }}
          display='flex'
          alignItems={{ base: 'center', md: isInternational ? 'flex-start' : 'center' }}
          justifyContent='center'
          sx={
            isConcertLike
              ? { bg: 'linear-gradient(to bottom right, #F7FAFC, #FFFFFF)' }
              : { bg: 'transparent' }
          }
        >
          {isMatch ? (
            // Match: Team Logos with VS
            <Flex
              direction='row'
              align='center'
              justify='center'
              gap={{ base: '4', md: '6', lg: '8' }}
              w='100%'
              flexWrap='wrap'
            >
              {/* Team A */}
              <Flex
                direction='column'
                gap={{ base: '2', md: '3' }}
                alignItems='center'
                flex='1'
                minW='80px'
              >
                <Box
                  w={{ base: '64px', md: '80px', lg: '96px' }}
                  h={{ base: '64px', md: '80px', lg: '96px' }}
                  borderRadius='full'
                  overflow='hidden'
                  boxShadow={{
                    base: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    md: '0 8px 20px rgba(0, 0, 0, 0.12)',
                  }}
                  backgroundColor='white'
                  p={{ base: '2', md: '2.5' }}
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  _hover={{
                    boxShadow: {
                      base: '0 8px 20px rgba(0, 0, 0, 0.15)',
                      md: '0 12px 28px rgba(0, 0, 0, 0.18)',
                    },
                    transform: 'translateY(-4px) scale(1.02)',
                  }}
                >
                  <Image
                    src={props.teamA.logo}
                    alt={`${props.teamA.name} logo`}
                    w='100%'
                    h='100%'
                    objectFit='contain'
                    borderRadius='full'
                    fallback={<PlaceholderTeamLogo />}
                  />
                </Box>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight='semibold'
                  textAlign='center'
                  noOfLines={2}
                  color='gray.700'
                  px='2'
                >
                  {props.teamA.name}
                </Text>
              </Flex>

              {/* VS Badge */}
              <Flex
                align='center'
                justify='center'
                minW={{ base: '40px', md: '48px' }}
                h={{ base: '40px', md: '48px' }}
                borderRadius='full'
                sx={{ bg: 'linear-gradient(to bottom right, #EDF2F7, #E2E8F0)' }}
                borderWidth='2px'
                borderColor='#CBD5E0'
              >
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight='bold'
                  color='gray.600'
                  letterSpacing='wider'
                >
                  VS
                </Text>
              </Flex>

              {/* Team B */}
              <Flex
                direction='column'
                gap={{ base: '2', md: '3' }}
                alignItems='center'
                flex='1'
                minW='80px'
              >
                <Box
                  w={{ base: '64px', md: '80px', lg: '96px' }}
                  h={{ base: '64px', md: '80px', lg: '96px' }}
                  borderRadius='full'
                  overflow='hidden'
                  boxShadow={{
                    base: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    md: '0 8px 20px rgba(0, 0, 0, 0.12)',
                  }}
                  backgroundColor='white'
                  p={{ base: '2', md: '2.5' }}
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  _hover={{
                    boxShadow: {
                      base: '0 8px 20px rgba(0, 0, 0, 0.15)',
                      md: '0 12px 28px rgba(0, 0, 0, 0.18)',
                    },
                    transform: 'translateY(-4px) scale(1.02)',
                  }}
                >
                  <Image
                    src={props.teamB.logo}
                    alt={`${props.teamB.name} logo`}
                    w='100%'
                    h='100%'
                    objectFit='contain'
                    borderRadius='full'
                    fallback={<PlaceholderTeamLogo />}
                  />
                </Box>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight='semibold'
                  textAlign='center'
                  noOfLines={2}
                  color='gray.700'
                  px='2'
                >
                  {props.teamB.name}
                </Text>
              </Flex>
            </Flex>
          ) : (
            // Concert: Event Image
            <Box
              w='100%'
              h={{ base: '240px', md: '360px', lg: '420px' }}
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <Image
                src={props.imageLink}
                alt={props.title}
                maxW='100%'
                maxH='100%'
                w='auto'
                h='auto'
                objectFit='contain'
                borderRadius={{ base: 'lg', md: 'xl' }}
              />
            </Box>
          )}
        </Box>

        {/* Content Section */}
        <Box
          flex='1'
          p={{ base: '4', md: '6' }}
          display='flex'
          flexDirection='column'
          gap={{ base: '3', md: '4' }}
        >
          {/* Title Section (Concert + International) */}
          {isConcertLike && (
            <Box>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight='bold'
                color='gray.800'
                mb={{ base: '1', md: '2' }}
                lineHeight='shorter'
              >
                {props.title}
              </Text>
              {props.subtitle && (
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color='gray.600'
                  fontWeight='medium'
                  lineHeight='tall'
                >
                  {props.subtitle}
                </Text>
              )}
            </Box>
          )}

          {/* Free Event Badge */}
          {props.isFreeEvent && (
            <Badge
              bg='teal.500'
              color='white'
              px={4}
              py={1.5}
              borderRadius='full'
              fontSize='sm'
              fontWeight='600'
              w='fit-content'
              textTransform='none'
            >
              <FormattedMessage id='freeEvent.badge' defaultMessage='Free Event' />
            </Badge>
          )}

          {/* Event Details */}
          <Flex direction='column' gap={{ base: '2.5', md: '3' }}>
            {props.hasTimeslots ? (
              <Flex align='center' gap={{ base: '2.5', md: '3' }}>
                <Icon
                  as={CalendarIcon}
                  w={{ base: '4', md: '5' }}
                  h={{ base: '4', md: '5' }}
                  color='blue.500'
                  flexShrink={0}
                />
                <Text fontSize={{ base: 'sm', md: 'md' }} color='gray.700' fontWeight='medium'>
                  حدث متعدد الاوقات
                </Text>
              </Flex>
            ) : (
              <>
                {props.date && (
                  <Flex align='center' gap={{ base: '2.5', md: '3' }}>
                    <Icon
                      as={CalendarIcon}
                      w={{ base: '4', md: '5' }}
                      h={{ base: '4', md: '5' }}
                      color='blue.500'
                      flexShrink={0}
                    />
                    <Text fontSize={{ base: 'sm', md: 'md' }} color='gray.700' fontWeight='medium'>
                      {props.date}
                    </Text>
                  </Flex>
                )}
                {props.time && (
                  <Flex align='center' gap={{ base: '2.5', md: '3' }}>
                    <Icon
                      as={ClockIcon}
                      w={{ base: '4', md: '5' }}
                      h={{ base: '4', md: '5' }}
                      color='purple.500'
                      flexShrink={0}
                    />
                    <Text fontSize={{ base: 'sm', md: 'md' }} color='gray.700' fontWeight='medium'>
                      {props.time}
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {props.city && (
              <Flex align='center' gap={{ base: '2.5', md: '3' }}>
                <Icon
                  as={Building2Icon}
                  w={{ base: '4', md: '5' }}
                  h={{ base: '4', md: '5' }}
                  color='black'
                  flexShrink={0}
                />
                <Text fontSize={{ base: 'sm', md: 'md' }} color='black' fontWeight='medium'>
                  {props.city}
                </Text>
              </Flex>
            )}
            {hasLocation && (
              <Flex
                align='center'
                gap={{ base: '2.5', md: '3' }}
                flexWrap='wrap'
                color='green.600'
                cursor={hasLocationMap ? 'pointer' : 'default'}
                {...(hasLocationMap && {
                  onClick: handleLocationClick,
                  onKeyDown: handleLocationKeyDown,
                  tabIndex: 0,
                  role: 'button',
                  'aria-label': `View location map for ${locationName}`,
                  px: '2',
                  py: '1.5',
                  mx: '-2',
                  borderRadius: 'md',
                  transition: 'all 0.2s ease',
                  _hover: {
                    bg: 'green.50',
                  },
                  _focus: {
                    outline: 'none',
                    boxShadow: '0 0 0 2px var(--chakra-colors-green-200)',
                  },
                  _active: {
                    bg: 'green.100',
                  },
                })}
              >
                <Icon
                  as={MapPin}
                  w={{ base: '4', md: '5' }}
                  h={{ base: '4', md: '5' }}
                  color='green.500'
                  flexShrink={0}
                />
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color='black'
                  fontWeight='medium'
                  textDecoration={hasLocationMap ? 'underline' : 'none'}
                  textDecorationColor='green.300'
                  textUnderlineOffset='2px'
                >
                  {locationName}
                </Text>
                {hasLocationMap && (
                  <Icon
                    as={ExternalLink}
                    w={{ base: '3.5', md: '4' }}
                    h={{ base: '3.5', md: '4' }}
                    color='green.400'
                    flexShrink={0}
                  />
                )}
              </Flex>
            )}
          </Flex>

          {/* Share Button */}
          <Flex mt={{ base: '2', md: '3' }}>
            <Button
              variant='secondaryMedium'
              leftIcon={<Share2 size={18} />}
              onClick={handleShareClick}
              onKeyDown={handleShareKeyDown}
              tabIndex={0}
              aria-label={isMatch ? 'مشاركة المباراة' : 'مشاركة الحدث'}
            >
              {isMatch ? 'مشاركة المباراة' : 'مشاركة الحدث'}
            </Button>
          </Flex>

          {/* Description */}
          {props.arDescription && (
            <Box
              mt={{ base: '2', md: '3' }}
              pt={{ base: '3', md: '4' }}
              borderTopWidth='1px'
              borderTopColor='gray.200'
            >
              {isInternational ? (
                <Box
                  dir='rtl'
                  textAlign='right'
                  fontSize={{ base: 'sm', md: 'md' }}
                  color='gray.600'
                  lineHeight='tall'
                  sx={{
                    ul: { listStyleType: 'disc', paddingInlineStart: '1.25em', marginY: '0.75em' },
                    ol: {
                      listStyleType: 'decimal',
                      paddingInlineStart: '1.25em',
                      marginY: '0.75em',
                    },
                    li: { marginY: '0.35em', paddingInlineStart: '0.25em' },
                    strong: { fontWeight: 'semibold', color: 'gray.800' },
                    p: { marginY: '0.75em' },
                  }}
                >
                  {parse(props.arDescription)}
                </Box>
              ) : (
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color='gray.600'
                  lineHeight='tall'
                  whiteSpace='pre-wrap'
                >
                  {props.arDescription}
                </Text>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Location Map Modal */}
      {props.locationHtml && (
        <LocationMapModal
          isOpen={isOpen}
          onClose={onClose}
          locationHtml={props.locationHtml}
          locationName={locationName}
        />
      )}
    </Box>
  );
};
